/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/file
 * @desc Column Definition for metadata file
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import FileActionCreate from 'labstep-web/components/File/Action/Create';
import { FileActionRemove } from 'labstep-web/components/File/Action/Remove';
import { FilePreviewContainer as FilePreview } from 'labstep-web/components/File/Preview';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { File } from 'labstep-web/models/file.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { generateNewDateString } from 'labstep-web/services/date.service';
import rules from 'labstep-web/services/validation/rules';
import { Label } from 'semantic-ui-react';

export const getMetadataFileColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.secondary,
      cornerIcon:
        ICONS[parentEntityClass as keyof typeof ICONS].primary,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }
      return metadata.value;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );
      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass]?.metadata_thread
              .idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }

        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };
        return (
          <TextPlaceholder
            onClick={() => {
              createOrUpdateValue({
                entityName: Metadata.entityName,
                body: {
                  type,
                  label: metadataLabel,
                  metadata_thread_id: metadataThreadId,
                },
                createProps: props,
                options: {
                  onSuccess: () => {
                    stopEditingAndFocus(params);
                    GridColumnDefinitionService.refreshCell(params);
                    params.api.startEditingCell({
                      rowIndex: params.node.rowIndex!,
                      colKey: params.column.getColId(),
                    });
                  },
                },
              });
            }}
          >
            Upload files
          </TextPlaceholder>
        );
      }

      const files = metadata ? metadata.filesGroupedByName : [];
      if (files.length > 1) {
        return (
          <Modal
            size="small"
            header="Files"
            viewComponent={({ toggleModal }) => (
              <TextPlaceholder onClick={toggleModal}>
                Multiple files
              </TextPlaceholder>
            )}
            content={({ toggleModal }) => (
              <div>
                {files.map((file: File) => (
                  <Label key={file.id}>
                    <FilePreview
                      file={file}
                      text={file.name}
                      noDiv
                      withDefault
                    />
                    <FileActionRemove
                      file={file}
                      actionComponentProps={{
                        type: 'icon',
                        icon: 'close',
                        elementProps: { noDiv: true },
                      }}
                      options={GridColumnDefinitionService.getOptions(
                        params,
                        () => {
                          toggleModal();
                        },
                      )}
                    />
                  </Label>
                ))}
              </div>
            )}
          />
        );
      } else if (files.length === 1) {
        const file = files[0];
        return (
          <Label key={file.id}>
            <FilePreview
              file={file}
              text={file.name}
              noDiv
              withDefault
            />
            <FileActionRemove
              file={file}
              actionComponentProps={{
                type: 'icon',
                icon: 'close',
                elementProps: { noDiv: true },
              }}
              options={GridColumnDefinitionService.getOptions(params)}
            />
          </Label>
        );
      }

      return (
        <TextPlaceholder
          onClick={() => {
            params.api.startEditingCell({
              rowIndex: params.node.rowIndex!,
              colKey: params.column.getColId(),
            });
          }}
        >
          Upload files
        </TextPlaceholder>
      );
    },
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }

      return (
        <FileActionCreate
          entityType={metadata.entityName}
          entityId={metadata.id}
          onFolderWatcherSelect={({ file }): void => {
            setValue('file_id', file.id, {
              entityName: metadata.entityName,
              id: metadata.id,
              createProps: {
                parentName:
                  params.node.data.metadata_thread.entityName,
                parentId: params.node.data.metadata_thread.idAttr,
              },
              options: GridColumnDefinitionService.getOptions(params),
            });
            stopEditingAndFocus(params);
          }}
          options={{
            onSuccess: () => {
              stopEditingAndFocus(params);
            },
          }}
          modalProps={{
            onClose: (): void => stopEditingAndFocus(params),
            open: true,
            viewComponent: null,
          }}
        />
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: (params: CellEditorParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (metadata) {
        setValue(
          'deleted_at',
          generateNewDateString(),
          {
            entityName: metadata.entityName,
            id: metadata.idAttr,
          },
          rules.metadata.date,
          GridColumnDefinitionService.getOptions(params),
        );
      }
      return null;
    },
  };
};
